import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Collapse, Nav, NavItem, NavLink, Navbar, NavbarBrand } from 'reactstrap';

import { Alert, Badge } from 'antd';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import axios from '../../axios';

import { hasAllKeys } from '../../helpers/common';

import { DATE_FORMAT, MAX_SET_TIMEOUT_DELAY } from '../../constants/common';
import messages from '../../constants/messages';
import { FEATURE_LIST } from '../../constants/subscription';
import { PROFILE_REQUIRED_FIELDS } from '../../constants/user';

import { useSetState } from '../../hooks/useSetState';

import { getAllEvents, setEventQueryParams } from '../../redux/actions/event';
import { setNotificationList } from '../../redux/actions/notification';
import { getUserReq, getUserTablePreferences } from '../../redux/actions/user';
import { fetchUserTableSettings } from '../../redux/actions/userSlice';

import { errorToastHandler } from '../../components/action_notifier';

import small_logo from '../../assets/images/Kraal-colour-white-icon.webp';
import kraalWhitelogo from '../../assets/images/Kraal-colour-white.webp';
import FeatureProvider from '../FeatureProvider';
import NotificationsDropdown from './NotificationsDropdown';
import UserDropdown from './UserDropdown';

dayjs.extend(utc);

const Header = props => {
  const dispatch = useDispatch();

  const notification = useSelector(state => state.notification);
  const { user, loading: userLoading } = useSelector(state => state.user);
  const { tasks } = useSelector(state => state.task);
  const { events } = useSelector(state => state.event);
  const { myPlan, loading: subscriptionLoading } = useSelector(state => state.subscription);

  const [bannerMessage, setBannerMessage] = useState(null);
  const [state, setState] = useSetState({
    isOpen: false,
    user: {},
    notifications: [],
    notification: { list: [] }
  });

  const eventTimersRef = useRef([]);
  const taskTimersRef = useRef([]);

  useEffect(() => {
    eventTimersRef.current.forEach(clearTimeout);
    eventTimersRef.current = [];

    const now = dayjs();

    events.forEach(event => {
      const eventTime = dayjs(event.start_at);
      const reminderTime = eventTime.subtract(5, 'minutes');
      const delay = reminderTime.diff(now);

      if (reminderTime.isAfter(now) && delay <= MAX_SET_TIMEOUT_DELAY) {
        const timer = setTimeout(() => {
          alert(`Event "${event.title}" is starting at ${dayjs(event.start_at).format('h:mma [on] M/D/YYYY')}`);
        }, delay);

        eventTimersRef.current.push(timer);
      }
    });

    return () => eventTimersRef.current.forEach(clearTimeout);
  }, [events]);

  useEffect(() => {
    taskTimersRef.current.forEach(clearTimeout);
    taskTimersRef.current = [];

    const now = dayjs();

    tasks.forEach(task => {
      const taskTime = dayjs(task.due_date);
      const reminderTime = taskTime.subtract(5, 'minutes');
      const delay = reminderTime.diff(now);

      if (reminderTime.isAfter(now) && delay <= MAX_SET_TIMEOUT_DELAY) {
        const timer = setTimeout(() => {
          alert(`Task "${task.title}" is ending at ${taskTime.format('h:mma [on] M/D/YYYY')}`);
        }, delay);

        taskTimersRef.current.push(timer);
      }
    });

    return () => taskTimersRef.current.forEach(clearTimeout);
  }, [tasks]);

  const getEvents = (startDate = dayjs().startOf('month'), endDate = dayjs().endOf('month')) => {
    const parsedDate = dayjs(endDate).format(DATE_FORMAT.DATETIME);
    const params = {
      startDate: dayjs.utc(startDate.format(DATE_FORMAT.DATETIME)).toISOString(),
      endDate: dayjs.utc(parsedDate, DATE_FORMAT.DATETIME).toISOString()
    };
    dispatch(setEventQueryParams(params));
    dispatch(getAllEvents(params));
  };

  const getNotifications = async () => {
    const response = await axios.get('notifications', {
      params: {
        query: 'notification_badge'
      }
    });

    setState({ notifications: response?.data, new: response?.data.length });
    dispatch(setNotificationList(response?.data));
  };

  const getUserData = async () => {
    try {
      const [res] = await Promise.all([
        dispatch(getUserReq()),
        dispatch(getUserTablePreferences()),
        dispatch(fetchUserTableSettings()),
        getEvents(),
        getNotifications()
      ]);

      if (res) {
        setState({ user: res });
      }
    } catch (error) {
      // TODO: Needs a solution for the proper handling of 403 error
      if (error?.response?.status === 403) return;
      errorToastHandler(error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  };

  useEffect(() => {
    getUserData();
  }, []);

  const toggle = () => setState(prev => ({ isOpen: !prev.isOpen }));

  const logout = () => {
    if (props.socket) {
      props.socket.disconnect();
    }

    localStorage.removeItem('token');
    window.location.replace('/auth/login');
  };

  const showMobilemenu = () => {
    document.getElementById('main-wrapper').classList.toggle('show-sidebar');
  };

  const sidebarHandler = () => {
    let element = document.getElementById('main-wrapper');
    switch (props.data.settings[0].sidebartype) {
      case 'full':
      case 'iconbar':
        element.classList.toggle('mini-sidebar');
        if (element.classList.contains('mini-sidebar')) {
          element.setAttribute('data-sidebartype', 'mini-sidebar');
        } else {
          element.setAttribute('data-sidebartype', props.data.settings[0].sidebartype);
        }
        break;

      case 'overlay':
      case 'mini-sidebar':
        element.classList.toggle('full');
        if (element.classList.contains('full')) {
          element.setAttribute('data-sidebartype', 'full');
        } else {
          element.setAttribute('data-sidebartype', props.data.settings[0].sidebartype);
        }
        break;

      default:
    }
  };

  let NOTIFICATIONS = [];

  const notificationList = notification?.list;
  const shortNotificationList = notificationList?.length ? notificationList?.slice(0, 4) : [];
  const notificationLength = notificationList?.length;

  const truncateText = string => {
    let maxSize = 52;
    if (string.length > maxSize) {
      string = string.substring(0, maxSize) + '...';
    }
    return string;
  };

  for (const not of shortNotificationList) {
    NOTIFICATIONS.push(
      <li key={not.id} style={{ padding: '10px 0' }}>
        <Link key={not.id} to={`/notification/${not.id}`}>
          <div className="d-flex align-items-baseline">
            <Badge
              dot
              status="processing"
              color={not.priority === 'medium' ? 'orange' : '#e73a1e'}
              style={{ marginRight: '10px' }}
            />
            <div className={'priority ' + not.priority}>
              <div className="circle"></div>
            </div>
            <div className="text">
              <span className="title">{truncateText(not.message)}</span>
              <br />
              <span className="text-small text-muted date">
                {dayjs(not.trigger_start_at).format(DATE_FORMAT.TIME_DATE)}
              </span>
            </div>
          </div>
        </Link>
      </li>
    );
  }

  let isDesktopView = true;
  if (document.getElementById('main-wrapper') && document.getElementById('main-wrapper').offsetWidth < 768) {
    isDesktopView = false;
  }

  useEffect(() => {
    if (userLoading || subscriptionLoading) return;

    const isProfileCompleted = hasAllKeys(props.user, PROFILE_REQUIRED_FIELDS);
    const isSubscription = !!myPlan?.user_plan_id;

    const bannerMessage =
      !isProfileCompleted && !isSubscription ? (
        <>
          Finish setting up your <Link to="/profile?edit=true">profile</Link> to start exploring everything our app has
          to offer.
        </>
      ) : isProfileCompleted && !isSubscription ? (
        <>
          We’re almost there! <Link to="/profile?plan=true">Select a subscription plan</Link> and enjoy your free trial
          — full access, no credit card required.
        </>
      ) : null;
    setBannerMessage(bannerMessage);
  }, [userLoading, subscriptionLoading]);

  return (
    <header className="topbar navbarbg" data-navbarbg={props.data.settings[0].navbarbg}>
      <Navbar
        className={'top-navbar ' + (props.data.settings[0].navbarbg === 'skin6' ? 'navbar-light' : 'navbar-dark')}
        expand="md">
        <div className="navbar-header" id="logobg" data-logobg={props.data.settings[0].logobg}>
          <a className="nav-toggler d-block d-md-none" onClick={showMobilemenu}>
            <i className="ti-menu ti-close" />
          </a>
          {!isDesktopView && (
            <NavbarBrand href="/">
              <b className="logo-icon">
                <img src={small_logo} alt="homepage" className="light-logo icon-logo" width="50" />
              </b>
              <span className="logo-text">
                <img src={kraalWhitelogo} className="light-logo expendlogo" alt="homepage" width="169" />
              </span>
            </NavbarBrand>
          )}
          <a className="topbartoggler d-block d-md-none" onClick={toggle}>
            <i className="ti-more" />
          </a>
        </div>
        <Collapse className="navbarbg" isOpen={state.isOpen} navbar data-navbarbg={props.data.settings[0].navbarbg}>
          <Nav className="float-left" navbar>
            <NavItem>
              <NavLink href="#" className="d-none d-md-block" onClick={sidebarHandler}>
                <i className="ti-menu" />
              </NavLink>
            </NavItem>
          </Nav>
          {!!bannerMessage && <Alert closable showIcon message={bannerMessage} type="info" className="info-banner" />}
          <Nav className="ml-auto float-right" navbar>
            <FeatureProvider name={FEATURE_LIST.NOTIFICATION_ICON}>
              <NotificationsDropdown count={notificationLength} list={NOTIFICATIONS} history={props.history} />
            </FeatureProvider>

            <UserDropdown user={user} handleLogOut={logout} />
          </Nav>
        </Collapse>
      </Navbar>
    </header>
  );
};

export default Header;
